'use client';

import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import { HorizontalSeparator } from '@/app_components/ui/Separator';
import { cn } from '@/app_lib/utils/utils';

interface CarouselProps {
  children?: ReactNode;
  className?: string;
  displayNum?: number;
  fullScroll?: boolean;
  hideSeparator?: boolean;
}
/**
 *
 * @param children - The children to render in the carousel.
 * @param className - The class name to apply to the carousel.
 * @param displayNum - The number of items to display in the carousel. Defaults to 4.
 * @param fullScroll - Whether to scroll the carousel to the full width of the container. This will scroll to index rather than using width. Defaults to false.
 * @param hideSeparator - Whether to hide the separator between the carousel and the content. Defaults to false.
 * @returns A carousel component.
 */
export const Carousel = ({
  children,
  className,
  displayNum = 4,
  fullScroll = false,
  hideSeparator = false,
}: CarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(false);
  const [index, setIndex] = useState(0);

  const scrollToIndex = useCallback((nextIndex: number) => {
    const container = scrollRef.current;

    if (!container) {
      return;
    }

    const childrenArray = Array.from(container.children) as HTMLElement[];

    const clamped = Math.max(0, Math.min(nextIndex, childrenArray.length - 1));
    const target = childrenArray[clamped];
    if (!target) {
      return;
    }

    const targetLeft = target.offsetLeft - container.offsetLeft; // align to start

    container.scrollTo({
      left: targetLeft,
    });

    setIndex(clamped);
  }, []);

  const scrollToWidth = useCallback((direction: 'left' | 'right') => {
    const container = scrollRef.current;

    if (!container) {
      return;
    }
    const firstChild = container.firstElementChild as HTMLElement | null;
    if (!firstChild) {
      return;
    }
    const cardWidth = firstChild.clientWidth;
    const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;
    container.scrollBy({ left: scrollAmount });
    return;
  }, []);

  const scrollTo = useCallback(
    (direction: 'left' | 'right') => {
      if (!fullScroll) {
        scrollToWidth(direction);
        return;
      }

      const delta = direction === 'left' ? -displayNum : displayNum;
      scrollToIndex(index + delta);
    },
    [scrollToIndex, index, displayNum, fullScroll, scrollToWidth],
  );

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;

    setIsLeftDisabled(scrollLeft === 0);
    setIsRightDisabled(scrollLeft + clientWidth >= scrollWidth - 1); // -1 for subpixel rendering
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    container.addEventListener('scroll', handleScroll);
    handleScroll();

    const resizeObserver = new ResizeObserver(() => {
      handleScroll();
    });
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [handleScroll]);

  const showButtons = !(isLeftDisabled && isRightDisabled);

  // NOTE: We need to pre-compute these in JS because Tailwind generates its css at build time.
  const mdCols = displayNum - 1 > 0 ? `calc((100% - ${displayNum - 2} * var(--gap)) / ${displayNum - 1})` : '100%';
  const lgCols = `calc((100% - ${displayNum - 1} * var(--gap)) / ${displayNum})`;

  return (
    <div className={cn('flex flex-col gap-lg', className)}>
      <div
        className='
          grid grid-flow-col
          overflow-x-auto md:overflow-x-hidden
          scrollbar-hidden scroll-smooth
          snap-x snap-mandatory
          gap-4 md:gap-8 [--gap:theme(spacing.8)]
          auto-cols-[75%]
          md:auto-cols-[var(--md-cols)] 
          lg:auto-cols-[var(--lg-cols)]
          -mx-4 md:mx-0
          pr-4 md:pr-0
        '
        ref={scrollRef}
        style={{
          // biome-ignore lint/suspicious/noExplicitAny: CSS custom property key requires type assertion
          ['--md-cols' as any]: mdCols,
          // biome-ignore lint/suspicious/noExplicitAny: CSS custom property key requires type assertion
          ['--lg-cols' as any]: lgCols,
        }}
      >
        {children}
      </div>
      <div className='flex flex-col gap-lg'>
        {showButtons && (
          <div className='hidden md:flex ml-auto gap-lg'>
            <ActionableIcon
              className='p-2 border rounded-full stroke-helper bg-secondary'
              disabled={isLeftDisabled}
              onClick={() => scrollTo('left')}
            >
              <Icon icon='ChevronLeft' />
            </ActionableIcon>
            <ActionableIcon
              className='p-2 border rounded-full stroke-helper bg-secondary'
              disabled={isRightDisabled}
              onClick={() => scrollTo('right')}
            >
              <Icon icon='ChevronRight' />
            </ActionableIcon>
          </div>
        )}
        {!hideSeparator && <HorizontalSeparator className='stroke-helper' />}
      </div>
    </div>
  );
};
